import { useEffect, useState } from 'react';
import api from '@/lib/api';

interface PickingWave {
  id: string;
  name: string;
  strategy: string;
  status: string;
  createdAt: string;
  _count: { tasks: number };
}

interface Warehouse {
  id: string;
  name: string;
  code: string;
}

interface Order {
  id: string;
  number: string;
  status: string;
}

const waveStatusLabels: Record<string, { label: string; color: string }> = {
  PENDING: { label: 'Pendente', color: 'bg-gray-100 text-gray-700' },
  IN_PROGRESS: { label: 'Em Progresso', color: 'bg-yellow-100 text-yellow-700' },
  COMPLETED: { label: 'Concluída', color: 'bg-green-100 text-green-700' },
  CANCELLED: { label: 'Cancelada', color: 'bg-red-100 text-red-700' },
};

const strategyLabels: Record<string, string> = {
  SINGLE_ORDER: 'Pedido Único',
  ZONE: 'Por Zona',
  BATCH: 'Lote',
  WAVE: 'Onda',
};

export default function PickingPage() {
  const [waves, setWaves] = useState<PickingWave[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedWave, setSelectedWave] = useState<string | null>(null);
  const [waveTasks, setWaveTasks] = useState<any[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [availableOrders, setAvailableOrders] = useState<Order[]>([]);
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [waveName, setWaveName] = useState('');

  useEffect(() => {
    api.get('/picking/waves').then((r) => {
      setWaves(r.data);
      setLoadingData(false);
    });
  }, []);

  useEffect(() => {
    api.get('/picking/waves').then((r) => {
      setWaves(r.data);
      setLoadingData(false);
    }).catch(() => setLoadingData(false));
  }, []);

  const openModal = async () => {
    try {
      const [whRes, ordersRes] = await Promise.all([
        api.get('/warehouses').catch(() => ({ data: {} })),
        api.get('/orders?status=EM_SEPARACAO').catch(() => ({ data: {} })),
      ]);
      setWarehouses(whRes.data || []);
      setAvailableOrders(ordersRes.data.data || []);
      setShowModal(true);
    } catch {
      setLoadingData(false);
    }
  };

  const createWave = async (e: React.FormEvent) => {
    e.preventDefault();
    await api.post('/picking/waves', {
      name: waveName,
      warehouseId: selectedWarehouse,
      orderIds: selectedOrders,
    });
    setShowModal(false);
    setWaveName('');
    setSelectedWarehouse('');
    setSelectedOrders([]);
    api.get('/picking/waves').then((r) => setWaves(r.data));
  };

  const viewTasks = async (waveId: string) => {
    setSelectedWave(waveId);
    const r = await api.get(`/picking/waves/${waveId}/tasks`);
    setWaveTasks(r.data);
  };

  const toggleOrder = (orderId: string) => {
    setSelectedOrders((prev) =>
      prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId]
    );
  };

  return (
    <>
    <div className="flex items-center justify-end mb-6">
        <button
          onClick={openModal}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
        >
          Criar Onda
        </button>
      </div>

      {loadingData ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-dark-100 overflow-hidden">
          <table className="w-full">
            <thead className="bg-dark-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-dark-500 uppercase">Nome</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-dark-500 uppercase">Estratégia</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-dark-500 uppercase">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-dark-500 uppercase">Tarefas</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-dark-500 uppercase">Criado em</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-100">
              {waves.map((wave, index) => (
                <tr
                  key={wave.id}
                  className={`${index % 2 === 0 ? 'bg-white' : 'bg-dark-50/50'} hover:bg-primary-50 cursor-pointer transition-colors`}
                  onClick={() => viewTasks(wave.id)}
                >
                  <td className="px-6 py-4 text-sm font-medium text-primary-600">{wave.name}</td>
                  <td className="px-6 py-4 text-sm text-dark-600">{strategyLabels[wave.strategy] || wave.strategy}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${waveStatusLabels[wave.status]?.color || 'bg-gray-100 text-gray-700'}`}>
                      {waveStatusLabels[wave.status]?.label || wave.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-dark-600 text-right">{wave._count?.tasks || 0}</td>
                  <td className="px-6 py-4 text-sm text-dark-500">{new Date(wave.createdAt).toLocaleDateString('pt-BR')}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {waves.length === 0 && (
            <div className="text-center py-12 text-dark-500">Nenhuma onda encontrada</div>
          )}
        </div>
      )}

      {selectedWave && (
        <div className="mt-6 bg-white rounded-xl shadow-sm border border-dark-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-dark-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-dark-500 uppercase">Tarefas da Onda</h3>
            <button onClick={() => setSelectedWave(null)} className="text-sm text-dark-400 hover:text-dark-600">Fechar</button>
          </div>
          <table className="w-full">
            <thead className="bg-dark-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-dark-500 uppercase">Produto</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-dark-500 uppercase">Endereço</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-dark-500 uppercase">Qtd</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-dark-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-100">
              {waveTasks.map((task) => (
                <tr key={task.id}>
                  <td className="px-6 py-4 text-sm text-dark-900">{task.product?.name || '-'}</td>
                  <td className="px-6 py-4 text-sm text-dark-600">{task.location?.code || '-'}</td>
                  <td className="px-6 py-4 text-sm text-dark-600 text-right">{task.quantity}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${task.status === 'COMPLETED' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                      {task.status === 'COMPLETED' ? 'Concluída' : 'Pendente'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {waveTasks.length === 0 && (
            <div className="text-center py-8 text-dark-500">Nenhuma tarefa</div>
          )}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg shadow-xl max-h-[80vh] overflow-y-auto">
            <h2 className="text-lg font-bold text-dark-900 mb-4">Criar Onda de Separação</h2>
            <form onSubmit={createWave} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-dark-700 mb-1">Nome</label>
                <input
                  type="text"
                  value={waveName}
                  onChange={(e) => setWaveName(e.target.value)}
                  className="w-full px-3 py-2 border border-dark-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-700 mb-1">Depósito</label>
                <select
                  value={selectedWarehouse}
                  onChange={(e) => setSelectedWarehouse(e.target.value)}
                  className="w-full px-3 py-2 border border-dark-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                >
                  <option value="">Selecione um depósito</option>
                  {warehouses.map((wh) => (
                    <option key={wh.id} value={wh.id}>{wh.name} ({wh.code})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-700 mb-2">Pedidos</label>
                <div className="border border-dark-200 rounded-lg max-h-48 overflow-y-auto">
                  {availableOrders.map((order) => (
                    <label key={order.id} className="flex items-center gap-3 px-3 py-2 hover:bg-dark-50 cursor-pointer border-b border-dark-100 last:border-0">
                      <input
                        type="checkbox"
                        checked={selectedOrders.includes(order.id)}
                        onChange={() => toggleOrder(order.id)}
                        className="rounded border-dark-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-dark-900">{order.number}</span>
                    </label>
                  ))}
                  {availableOrders.length === 0 && (
                    <div className="text-center py-4 text-dark-500 text-sm">Nenhum pedido disponível</div>
                  )}
                </div>
              </div>
              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border border-dark-200 rounded-lg text-sm font-medium hover:bg-dark-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
                >
                  Criar Onda
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
